import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  Box,
  Chip,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveAltOutlinedIcon from "@mui/icons-material/SaveAltOutlined";
import LinkIcon from "@mui/icons-material/Link";
import SearchIcon from "@mui/icons-material/Search";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import FileTypeIcon from "./FileTypeIcon";
import {
  formatBytes,
  formatDateTime,
  formatRelativeTime,
  getFileExtension,
  getFileCategory,
  buildFileUrl,
  copyFileLink,
  parseDate,
  FILES_HEADER_HEIGHT,
} from "./filesUtils";

const CATEGORY_COLORS = {
  image: { bg: "#f3e8ff", color: "#7c3aed" },
  document: { bg: "#dbeafe", color: "#1d4ed8" },
  code: { bg: "#d1fae5", color: "#047857" },
  video: { bg: "#fee2e2", color: "#b91c1c" },
  audio: { bg: "#fef3c7", color: "#b45309" },
  archive: { bg: "#f1f5f9", color: "#475569" },
  text: { bg: "#cffafe", color: "#0e7490" },
  other: { bg: "#f1f5f9", color: "#64748b" },
};

export default function FilesTable({
  files,
  filesLoading,
  totalFileSize,
  headerTitle,
  emptyMessage,
  selectable,
  selectedFiles,
  onSelectFile,
  previewFile,
  onClickFile,
  headerActions,
  onSnackbar,
  onDeleteFile,
  canDelete,
  searchValue,
  onSearchChange,
  paginationMode,
  rowCount,
  paginationModel,
  onPaginationModelChange,
  sortingMode,
  sortModel,
  onSortModelChange,
  displayedFileCount,
}) {
  const [internalSearchValue, setInternalSearchValue] = useState("");
  const serverMode = paginationMode === "server" || sortingMode === "server";
  const effectiveSearchValue = searchValue ?? internalSearchValue;

  useEffect(() => {
    if (searchValue === undefined) setInternalSearchValue("");
  }, [files, searchValue]);

  const dedupedFiles = useMemo(() => {
    if (!files) return [];
    const seen = new Set();
    const out = [];
    for (const f of files) {
      if (f?.id == null || seen.has(f.id)) continue;
      seen.add(f.id);
      out.push(f);
    }
    return out;
  }, [files]);

  const filteredFiles = useMemo(() => {
    if (serverMode || !effectiveSearchValue.trim()) return dedupedFiles;
    const searchLower = effectiveSearchValue.toLowerCase();
    return dedupedFiles.filter((file) =>
      (file.fileName || "").toLowerCase().includes(searchLower)
    );
  }, [dedupedFiles, effectiveSearchValue, serverMode]);

  const handleSearchChange = useCallback(
    (event) => {
      const nextValue = event.target.value;
      if (onSearchChange) {
        onSearchChange(nextValue);
      } else {
        setInternalSearchValue(nextValue);
      }
    },
    [onSearchChange]
  );

  const handleCopyLink = useCallback(
    async (fileId) => {
      const ok = await copyFileLink(fileId);
      if (onSnackbar) {
        onSnackbar(
          ok ? "File link copied to clipboard" : "Failed to copy file link",
          ok ? "success" : "error"
        );
      }
    },
    [onSnackbar]
  );

  const selectedSize = useMemo(() => {
    if (!selectable || !selectedFiles?.length) return 0;
    return selectedFiles.reduce((sum, id) => {
      const f = files.find((file) => file.id === id);
      return sum + (f?.fileSize || 0);
    }, 0);
  }, [selectable, selectedFiles, files]);

  const columns = useMemo(
    () => [
      {
        field: "fileName",
        headerName: "Name",
        flex: 1,
        minWidth: 200,
        sortComparator: (a, b) => (a || "").localeCompare(b || ""),
        renderCell: (params) => {
          const file = params.row;
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                minWidth: 0,
                width: "100%",
                position: "relative",
              }}
            >
              <FileTypeIcon fileName={file.fileName} />
              <Tooltip title={file.fileName || "Unnamed file"}>
                <Typography
                  noWrap
                  sx={{ fontSize: "0.8125rem", flex: 1, minWidth: 0 }}
                >
                  {file.fileName || "Unnamed file"}
                </Typography>
              </Tooltip>
              <Box
                className="row-actions"
                data-no-preview="true"
                onClick={(e) => e.stopPropagation()}
                sx={{
                  display: "none",
                  alignItems: "center",
                  gap: 0.25,
                  px: 0.5,
                  py: 0.25,
                  bgcolor: "background.paper",
                  borderRadius: 1.5,
                  boxShadow:
                    "0 1px 4px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.06)",
                  flexShrink: 0,
                }}
              >
                <Tooltip title="Copy link">
                  <IconButton
                    size="small"
                    onClick={() => handleCopyLink(file.id)}
                    sx={{ color: "text.secondary" }}
                  >
                    <LinkIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download">
                  <IconButton
                    size="small"
                    component="a"
                    href={buildFileUrl(file.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: "text.secondary" }}
                  >
                    <SaveAltOutlinedIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                {canDelete && onDeleteFile && (
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={() => onDeleteFile(file)}
                      sx={{
                        color: "text.secondary",
                        "&:hover": { color: "error.main" },
                      }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          );
        },
      },
      {
        field: "type",
        headerName: "Type",
        width: 90,
        sortable: true,
        valueGetter: (_value, row) => getFileExtension(row?.fileName),
        renderCell: (params) => {
          const ext = params.value;
          if (!ext)
            return (
              <Typography variant="body2" sx={{ color: "text.disabled" }}>
                -
              </Typography>
            );
          const category = getFileCategory(params.row.fileName);
          const chipColors = CATEGORY_COLORS[category] || CATEGORY_COLORS.other;
          return (
            <Chip
              label={ext}
              size="small"
              sx={{
                height: 22,
                fontSize: "0.6875rem",
                fontWeight: 600,
                bgcolor: chipColors.bg,
                color: chipColors.color,
                borderRadius: 1,
                textTransform: "uppercase",
              }}
            />
          );
        },
      },
      {
        field: "fileSize",
        headerName: "Size",
        width: 100,
        sortable: true,
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", fontSize: "0.8125rem" }}
          >
            {formatBytes(params.value)}
          </Typography>
        ),
      },
      {
        field: "createDate",
        headerName: "Modified",
        width: 170,
        sortable: true,
        sortComparator: (a, b) => {
          const dateA = parseDate(a);
          const dateB = parseDate(b);
          if (!dateA && !dateB) return 0;
          if (!dateA) return -1;
          if (!dateB) return 1;
          return dateA - dateB;
        },
        renderCell: (params) => (
          <Tooltip title={formatDateTime(params.value)}>
            <Typography variant="body2" sx={{ fontSize: "0.8125rem" }}>
              {formatRelativeTime(params.value)}
            </Typography>
          </Tooltip>
        ),
      },
    ],
    [handleCopyLink, canDelete, onDeleteFile]
  );

  const handleRowClick = useCallback(
    (params, event) => {
      if (event.target.closest("[data-no-preview]")) return;
      if (onClickFile) onClickFile(params.row);
    },
    [onClickFile]
  );

  const handleSelectionChange = useCallback(
    (newSelection) => {
      if (!selectable || !onSelectFile) return;
      const prev = selectedFiles || [];
      const added = newSelection.filter((id) => !prev.includes(id));
      const removed = prev.filter((id) => !newSelection.includes(id));
      [...added, ...removed].forEach((id) => onSelectFile(id));
    },
    [selectable, onSelectFile, selectedFiles]
  );

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2.5,
          height: FILES_HEADER_HEIGHT,
          minHeight: FILES_HEADER_HEIGHT,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
          gap: 2,
          flexShrink: 0,
          boxSizing: "border-box",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
          <Typography sx={{ fontWeight: 600, fontSize: "0.9375rem" }}>
            {headerTitle}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", fontSize: "0.8125rem" }}
          >
            {selectable && selectedFiles?.length > 0 ? (
              <>
                {selectedFiles.length} selected &middot;{" "}
                {formatBytes(selectedSize)}
              </>
            ) : (
              <>
                {displayedFileCount ?? filteredFiles.length} file
                {(displayedFileCount ?? filteredFiles.length) !== 1
                  ? "s"
                  : ""}{" "}
                &middot; {formatBytes(totalFileSize)}
              </>
            )}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {headerActions}
          <TextField
            size="small"
            placeholder="Search files..."
            value={effectiveSearchValue}
            onChange={handleSearchChange}
            sx={{
              width: 200,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: "background.default",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    fontSize="small"
                    sx={{ color: "text.disabled" }}
                  />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>

      {/* DataGrid */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        {filesLoading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 8,
            }}
          >
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 2 }}>
              Loading files...
            </Typography>
          </Box>
        ) : filteredFiles.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 8,
              textAlign: "center",
            }}
          >
            <FolderOpenIcon
              sx={{ fontSize: 48, color: "text.disabled", mb: 1 }}
            />
            <Typography variant="body1" sx={{ color: "text.secondary" }}>
              {effectiveSearchValue
                ? "No files match your search"
                : emptyMessage || "No files found"}
            </Typography>
          </Box>
        ) : (
          <DataGrid
            rows={filteredFiles}
            columns={columns}
            getRowId={(row) => row.id}
            rowHeight={40}
            columnHeaderHeight={36}
            disableColumnMenu
            disableColumnResize
            disableRowSelectionOnClick
            checkboxSelection={selectable}
            rowSelectionModel={selectedFiles || []}
            onRowSelectionModelChange={handleSelectionChange}
            onRowClick={handleRowClick}
            paginationMode={paginationMode}
            rowCount={rowCount}
            paginationModel={paginationModel}
            onPaginationModelChange={onPaginationModelChange}
            sortingMode={sortingMode}
            sortModel={sortModel}
            onSortModelChange={onSortModelChange}
            initialState={{
              sorting: { sortModel: [{ field: "createDate", sort: "desc" }] },
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            pageSizeOptions={[25, 50, 100]}
            getRowClassName={(params) =>
              previewFile?.id === params.id ? "row-previewing" : ""
            }
            sx={{
              border: "none",
              "& .MuiDataGrid-columnHeaders": {
                bgcolor: "background.default",
                borderBottom: 1,
                borderColor: "divider",
                minHeight: "36px !important",
                maxHeight: "36px !important",
              },
              "& .MuiDataGrid-columnHeader": {
                py: 0,
                "&:focus, &:focus-within": { outline: "none" },
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontSize: "0.75rem",
                fontWeight: 500,
                color: "text.secondary",
              },
              "& .MuiDataGrid-columnSeparator": {
                display: "none",
              },
              "& .MuiDataGrid-cell": {
                borderColor: "divider",
                py: 0,
                display: "flex",
                alignItems: "center",
                lineHeight: 1,
                "&:focus, &:focus-within": { outline: "none" },
              },
              "& .MuiDataGrid-cell .MuiDataGrid-cellContent": {
                display: "flex",
                alignItems: "center",
              },
              "& .MuiDataGrid-cell[data-field='createDate']": {
                pr: 3,
              },
              "& .MuiDataGrid-columnHeader[data-field='createDate']": {
                pr: 3,
              },
              "& .MuiDataGrid-row": {
                cursor: onClickFile ? "pointer" : "default",
                "&:hover .row-actions": { display: "flex" },
              },
              "& .MuiDataGrid-row.row-previewing": {
                borderLeft: "3px solid",
                borderLeftColor: "primary.main",
                bgcolor: "action.selected",
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: 1,
                borderColor: "divider",
                minHeight: 40,
              },
              "& .MuiTablePagination-root": {
                fontSize: "0.8125rem",
              },
              "& .MuiDataGrid-overlay": {
                bgcolor: "transparent",
              },
            }}
          />
        )}
      </Box>
    </Box>
  );
}
